import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { useLocationResourceStore } from '~/client/src/shared/hooks/useLocationResourceStore'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import type InitialState from '~/client/src/shared/stores/InitialState'

import ResourcePicker from '../ResourcePicker/ResourcePicker'
import { withKeepSelected } from '../ResourcePicker/resourceWorkflowVisibility'
import { useResourcePickerCards } from '../ResourcePicker/useResourceRenderItem'

interface IProps {
  mode: 'single' | 'multi'
  selectedIds: string[]
  onSelect: (ids: string[]) => void
  onClose: () => void
  /** Hidden, with everything under it, unless selected. */
  excludedSubtreeOf?: string | null
  /** Only these are offered, plus whatever is selected. */
  allowedIds?: readonly string[]
  allowEmptySelection?: boolean
}

// Walks the location resources, not the legacy kind stores: those miss project-added kinds
// and would cut a subtree short. A Set ends the walk on corrupt parent cycles.
function getSubtreeIds(
  records: readonly IResourceRecord[],
  rootId: string,
): Set<string> {
  const childrenByParentId = new Map<string, string[]>()
  records.forEach(({ id, parent }) => {
    if (!parent?.parentId) return
    const children = childrenByParentId.get(parent.parentId)
    if (children) children.push(id)
    else childrenByParentId.set(parent.parentId, [id])
  })

  const ids = new Set([rootId])
  for (const id of ids) {
    childrenByParentId.get(id)?.forEach(childId => ids.add(childId))
  }
  return ids
}

const LocationResourcePicker: React.FC<IProps> = ({
  mode,
  selectedIds,
  onSelect,
  onClose,
  excludedSubtreeOf,
  allowedIds,
  allowEmptySelection,
}) => {
  const { state, locationAttributesStore } = React.useContext(
    MobXProviderContext,
  ) as { state: InitialState; locationAttributesStore: LocationAttributesStore }
  const { records, loading, schema } = useLocationResourceStore(
    state.activeProject?.id,
  )
  // TODO(STX1-680): drop once picked ids display from location resources. Until then they
  // display through the legacy kind stores, so a location those do not hold (a
  // project-added kind) would be picked and then render blank.
  const legacyLocationsById = locationAttributesStore.hierarchyAllAttributesMap

  const { properties, propertiesReady, ready, renderItem } =
    useResourcePickerCards(ResourceTypeId.LOCATIONS, schema)

  // Keyed by content: hosts pass a fresh selectedIds each render, and a new predicate
  // re-projects every record.
  const selectedKey = selectedIds.join(',')
  const allowedKey = allowedIds?.join(',')
  const recordPredicate = React.useMemo(() => {
    const excluded = excludedSubtreeOf
      ? getSubtreeIds(records, excludedSubtreeOf)
      : null
    const allowed = allowedIds ? new Set(allowedIds) : null
    return withKeepSelected(
      record =>
        !!legacyLocationsById[record.id] &&
        !excluded?.has(record.id) &&
        (!allowed || allowed.has(record.id)),
      selectedIds,
    )
  }, [legacyLocationsById, records, excludedSubtreeOf, allowedKey, selectedKey])

  return (
    <ResourcePicker
      // The picker seeds its selection once; remount when the host's value changes under
      // it. Fine for single-mode hosts, which close on select. A multi host would lose
      // in-progress toggles, so re-sync inside useResourcePickerState before adding one.
      key={selectedKey}
      records={records}
      resourceTypeId={ResourceTypeId.LOCATIONS}
      mode={mode}
      selectedIds={selectedIds}
      onSelect={onSelect}
      loading={loading || !ready}
      properties={properties}
      propertiesReady={propertiesReady}
      resourceSchema={schema}
      recordPredicate={recordPredicate}
      allowEmptySelection={allowEmptySelection}
    >
      <ResourcePicker.Toolbar />
      <ResourcePicker.List
        renderItem={renderItem}
        countLabel={Localization.translator.locations}
      />
      <ResourcePicker.EmptyState />
      <ResourcePicker.Footer onCancel={onClose} />
    </ResourcePicker>
  )
}

export default observer(LocationResourcePicker)
